import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTeamsStore } from '@/stores/teamsStore';
import { useUsersStore } from '@nimbitlabs/features';
import { TeamFormData, TeamMember } from '@/types/team';
import { Users, Plus, X, ArrowLeft } from 'lucide-react';
import { AddTeamMemberModal } from '@/components/AddTeamMemberModal';
import { Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, TagInput, Textarea, ErrorAlert } from '@nimbitlabs/ui';

export const CreateTeam: React.FC = () => {
  const navigate = useNavigate();
  const { addTeam } = useTeamsStore();
  const { users } = useUsersStore();
  
  const [formData, setFormData] = useState<TeamFormData>({
    name: '',
    description: '',
    tags: [],
    email: '',
    lead: '',
    projects: [],
    members: [],
    links: []
  });

  const [error, setError] = useState<unknown>(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  const handleAddMember = async (userId: string, role: string) => {
    // Find the user details
    const user = users?.find(u => u.id === userId);
    if (!user) return;
    
    const member: TeamMember = {
      id: user.id,
      name: user.name,
      email: user.email,
      role,
      avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.name)}`
    };
    
    setFormData({
      ...formData,
      members: [...(formData.members || []), member]
    });
  };

  const handleRemoveMember = (email: string) => {
    setFormData({
      ...formData,
      members: formData.members?.filter(m => m.email !== email) || []
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await addTeam(formData);
      navigate('/teams');
    } catch (err) {
      console.error('Error creating team:', err);
      setError(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/teams">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Teams
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create New Team</CardTitle>
          <CardDescription>
            Create a new team to organize your services and members.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorAlert error={error} className="mb-4" />
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Team Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Frontend Team"
                required
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this team works on..."
                required
                maxLength={1000}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <TagInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData({ ...formData, tags })}
                placeholder="Add tags (e.g. frontend, ui, react)"
              />
            </div>

            <div className="space-y-2">
              <Label>Projects</Label>
              <TagInput
                tags={formData.projects || []}
                onTagsChange={(projects) => setFormData({ ...formData, projects })}
                placeholder="Add projects (e.g. website, mobile-app)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead">Team Lead *</Label>
              <Input
                id="lead"
                value={formData.lead}
                onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                placeholder="John Doe"
                required
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Team Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="team@company.com"
                required
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Team Members
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddMemberModal(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </div>
              {formData.members && formData.members.length > 0 ? (
                <div className="space-y-3">
                  {formData.members.map((member, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 rounded-lg border border-border/50 bg-background/50 hover:border-border transition-colors"
                    >
                      <Avatar className="h-10 w-10">
                        {member.avatar && <AvatarImage src={member.avatar} alt={member.name} />}
                        <AvatarFallback className="bg-primary/10 text-primary font-medium">
                          {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{member.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0">{member.role}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.email)}
                        className="shrink-0 h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No members added yet</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/teams')}>
                Cancel
              </Button>
              <Button type="submit">
                Create Team
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <AddTeamMemberModal
        open={showAddMemberModal}
        onOpenChange={setShowAddMemberModal}
        existingMembers={formData.members || []}
        onAddMember={handleAddMember}
      />
    </div>
  );
};
